/**
 * InterpreterOverlay — web. Two glass panels separated by the "guide equator";
 * the top panel is rotated 180° for the person across the table.
 *
 * Now renders the v2 utterance stream via the @wg/hooks/interpreter helpers.
 * RN currently implements v2 independently and does not yet import these
 * helpers, so cross-client reducer convergence remains a follow-up.
 *
 * Previously this consumed the v1 `transcript` + `partials` shape. `partials`
 * was always empty in practice — nothing has emitted `interpreter:partial`
 * since the 2026-07-24 redesign — so the live streaming text never appeared,
 * and the equator read "Listening" for the entire session because it keyed off
 * `agentState.translating`, which also never arrives. Both now derive from the
 * utterances themselves.
 *
 * Cultural notes are gone: the worker stopped emitting them, and rendering a
 * branch for a dead event is what hid the drift for weeks.
 */

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  isSettled,
  panelTexts,
  type InterpreterUtterance,
} from '@wg/hooks/interpreter';

import Icon from '../../common/Icon';
import { useInterpreter } from '../../context/InterpreterContext';
import './InterpreterOverlay.css';

interface Props {
  visible: boolean;
  onClose: () => void;
}

/** Which half of the table this panel belongs to. */
type PanelSide = 'traveler' | 'local';

function langDisplay(code: string): string {
  if (!code) return '';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

function SpeakerPanel({
  side,
  language,
  rotated,
}: {
  side: PanelSide;
  language: string;
  rotated?: boolean;
}) {
  const { state } = useInterpreter();
  const listRef = useRef<HTMLDivElement>(null);

  // BOTH panels render EVERY utterance — correct for a table-top interpreter:
  // each side sees its own words large and the other side's translation large.
  const entries = state.utterances;
  const isEmpty = entries.length === 0;
  const langName = langDisplay(language);
  const roleLabel = side === 'traveler' ? 'You' : 'Local';

  // Scroll on CONTENT change, not just count: a growing partial keeps the same
  // id, so watching length alone let long streaming turns run below the fold.
  const contentKey = useMemo(
    () => entries.map(u => `${u.id}:${u.revision}`).join('|'),
    [entries],
  );
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [contentKey]);

  return (
    <div className={`wg-interp-panel${rotated ? ' rotated' : ''}`}>
      <div className="wg-interp-header">
        <span>{langName}</span>
        <span className="wg-interp-header-divider">•</span>
        <span>{roleLabel}</span>
      </div>

      {isEmpty ? (
        <div className="wg-interp-empty">
          {side === 'traveler'
            ? 'Speak when ready — your translation appears here.'
            : 'Waiting for the other party to speak…'}
        </div>
      ) : (
        <div className="wg-interp-list" ref={listRef}>
          {entries.map(u => (
            <UtteranceRow key={u.id} utterance={u} side={side} />
          ))}
        </div>
      )}
    </div>
  );
}

function UtteranceRow({
  utterance,
  side,
}: {
  utterance: InterpreterUtterance;
  side: PanelSide;
}) {
  const { primary, echo } = panelTexts(utterance, side);
  if (!primary && !echo) return null;

  // Unsettled text is dimmed so the reader can tell what may still change —
  // the established convention for re-translating captions.
  const pending = !isSettled(utterance);

  return (
    <div className="wg-interp-entry">
      <div
        className={`wg-interp-entry-original${pending ? ' partial' : ''}`}
        data-utterance-id={utterance.id}
        data-revision={utterance.revision}
        data-final={utterance.final ? '1' : '0'}
      >
        {primary}
      </div>
      {echo && echo !== primary && (
        <div className="wg-interp-entry-translated">{echo}</div>
      )}
    </div>
  );
}

export default function InterpreterOverlay({ visible, onClose }: Props) {
  const { state, deactivate } = useInterpreter();

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  if (!visible) return null;

  // Derived from the stream rather than a dead `agentState` event.
  const anyPending = state.utterances.some(u => !isSettled(u));
  const equatorLabel = anyPending ? 'Translating' : 'Listening';

  return createPortal(
    <div className="wg-interp" role="dialog" aria-modal="true">
      <div className="wg-interp-topbar">
        <span className="wg-interp-mode-icon">
          <Icon
            name="language-outline"
            size={20}
            color="rgba(255,255,255,0.6)"
          />
        </span>
        <button
          type="button"
          className="wg-interp-close"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" size={20} color="#fff" />
        </button>
      </div>

      <SpeakerPanel side="local" language={state.localLang} rotated />

      <div className="wg-interp-equator">
        <div className="wg-interp-equator-line" />
        <span data-equator-state={anyPending ? 'translating' : 'listening'}>
          {equatorLabel}
        </span>
        <div className="wg-interp-equator-line" />
      </div>

      <SpeakerPanel side="traveler" language={state.userLang} />

      <div className="wg-interp-controls">
        <button
          type="button"
          className="danger"
          onClick={async () => {
            await deactivate();
            onClose();
          }}
        >
          End interpreter
        </button>
      </div>
    </div>,
    document.body,
  );
}
